import { motion } from "framer-motion";
import { TrendingDown, HeartCrack, Flame, Users } from "lucide-react";

const costs = [
  {
    icon: TrendingDown,
    text: "Trauma cycles continue",
    color: "text-destructive",
  },
  {
    icon: HeartCrack,
    text: "People leave faith",
    color: "text-destructive",
  },
  {
    icon: Flame,
    text: "Leaders burn out or become abusive",
    color: "text-destructive",
  },
  {
    icon: Users,
    text: "Families are torn apart",
    color: "text-destructive",
  },
];

export function RealCost() {
  return (
    <section className="py-28 md:py-36 bg-background">
      <div className="container px-6">
        <div className="max-w-4xl mx-auto">
          {/* Eyebrow */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center text-xs font-bold uppercase tracking-[0.35em] text-destructive mb-6"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            What's at Stake
          </motion.p>

          {/* Headline */}
          <motion.h2
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-[0.95] tracking-[-0.03em] text-foreground mb-16"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            The Real Cost of{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-destructive italic">Unsafe</span>
              <motion.svg
                className="absolute -bottom-2 left-0 w-full z-0"
                viewBox="0 0 200 8"
                fill="none"
                preserveAspectRatio="none"
              >
                <motion.path
                  d="M2 5C40 2 80 7 100 4C120 1 160 7 198 3"
                  stroke="hsl(0, 84%, 60%)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.5 }}
                />
              </motion.svg>
            </span>
            {" "}Churches
          </motion.h2>

          {/* Cost items — staggered, dramatic */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {costs.map((cost, i) => (
              <motion.div
                key={cost.text}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 * i, type: "spring", damping: 15 }}
                className="group relative bg-card border border-border/40 rounded-2xl p-6 hover:border-destructive/30 transition-all duration-500 hover:shadow-[0_20px_60px_-15px_rgba(239,68,68,0.15)]"
              >
                {/* Top accent bar */}
                <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-destructive/40 to-transparent" />

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center shrink-0 group-hover:bg-destructive/20 transition-colors duration-300">
                    <cost.icon className="w-6 h-6 text-destructive" />
                  </div>
                  <p
                    className="text-lg md:text-xl font-bold text-foreground pt-2"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {cost.text}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom statement */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mt-16 text-lg md:text-xl italic text-muted-foreground max-w-2xl mx-auto"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Every day without reform is another day a survivor suffers in silence.
          </motion.p>
        </div>
      </div>
    </section>
  );
}
